import { getWallPath } from "building-generation/elements/walls/wall";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { FloorAssemblyHashParameters } from "building-types/floors/floorAssemblyTypes";
import { FloorAssemblyType, type FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import { SpanDirection, StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type {
  FloorFinishAssemblyTable,
  FloorFinishTable,
  FloorSystem,
  HalfEdgeToFloorPath,
} from "building-types/floors/floorSystemTypes";
import type { Floor } from "building-types/floors/floorTypes";
import type { FrameFloorAssemblyEntity } from "building-types/floors/frameFloorAssemblyTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import { SlabType } from "building-types/foundations/foundationEnums";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { StandardLumberCounterSet } from "building-types/framing/lumberTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import type { PlanarGraphLevelAssociations } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { TargetedPorchSettings } from "building-types/porches/porch";
import type { RoomClassFloorAssemblyGroupTable } from "building-types/rooms/roomClassFloorAssemblyGroupTable";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { enumValues } from "common/utils/enum";
import type { Mutable } from "common/utils/types";
import {
  floorAssemblyFloorPlateThicknessesByComponents,
  floorAssemblyJoistComponentHeight,
  floorAssemblyTop,
  voidFloorAssemblyNew,
} from "doc/elements/floor/floorAssembly";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { aabb2ByPolyLine2s, aabb2Grow, aabb2Overlaps } from "geom/aabb2";
import type { Face } from "geom/brep";
import { faceNew } from "geom/brep";
import {
  classifyContainmentPolyLine2PolyLine2,
  classifyContainmentPolyLine2PolyLine2Fast,
  PolyLine2Containment,
  PolyLine2PolyLine2ContainmentClass,
} from "geom/containment";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { extrudeFace } from "geom/extrude";
import { offsetPolyLine2VariableStep } from "geom/offset";
import type { PlanarGraph, PlanarGraphLoops } from "geom/planarGraph";
import {
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgesCollinear,
  planarGraphHalfEdgeStart,
  planarGraphHalfEdgeStripToPolyLine2,
  planarGraphLoopsReverse,
  planarGraphNextCollinearHalfEdgeInLoop,
  planarGraphNextHalfEdgeInLoop,
  planarGraphPrevCollinearHalfEdgeInLoop,
  planarGraphPrevHalfEdgeInLoop,
  planarGraphTraceFaceGroupPerimeter,
} from "geom/planarGraph";
import { planeNewXY } from "geom/plane";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2WindingDir,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Rect2 } from "geom/rect2Types";
import { slabTypeAtRoomClass } from "../foundation/slabUtils";
import { bottomLevelId, groundLevelId } from "../level/levelTools";
import {
  detectFloorPathOffsets,
  floorPathNew,
  floorPlateNew,
  nonZeroFloorThickness,
  solidFramingFloorNew,
  solidSlabFloorNew,
  voidFloorNew,
} from "./floor";
import { makeRoomFloorFinishes } from "./floorFinishAssembly";
import { makeFloorFraming } from "./floorFraming";
import { floorPathOpposite } from "./floorPathUtils";

interface MakeFloorSystemOptions {
  readonly levelId: string;
  readonly levels: Table<Level>;
  readonly planarGraph: PolygonPlanarGraph;
  readonly planarGraphLevelAssociations: PlanarGraphLevelAssociations;
  readonly rooms: Room[];
  readonly roomLevelIndices: Table<number>;
  readonly roomFloorFinishAssemblies?: FloorFinishAssemblyTable;
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly roomClassFloorAssemblyGroups: RoomClassFloorAssemblyGroupTable;
  readonly wallSystem: WallSystem;
  readonly settings: MakeFloorSystemSettings;
  readonly interiorPalette: InteriorPalette;
  readonly exteriorPalette: ExteriorPalette;
  readonly lumberCounters?: StandardLumberCounterSet;
  readonly floorPaths: HalfEdgeToFloorPath;
  readonly targetedPorchSettings: TargetedPorchSettings;
  readonly frameFloorAssemblies: Table<FrameFloorAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly slabComponents: Table<SlabComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
}

interface MakeFloorSystemSettings {
  readonly wallSettings: WallSettings;
  readonly porchSettings: PorchSettingsBase;
  readonly rearPorchSettings: PorchSettingsBase | undefined;
  readonly columnSettings: ColumnCatalogSettings;
  readonly foundationSettings: FoundationSettings;
}

export function floorSystemNew(props: Required<Partial<FloorSystem>>): FloorSystem {
  return { ...props };
}

export function makeFloorRails(opts: MakeFloorSystemOptions): FloorSystem {
  const floors = makeFloors(opts);

  return floorSystemNew({
    levelId: opts.levelId,
    roomClassFloors: floors,
    roomFloorFinishes: {},
  });
}

interface FloorPathLookupDataStructures {
  readonly rooms: Room[];
  readonly planarGraphFaceToRoom: number[];
  readonly roomToPlanarGraphFace: number[];
}

function initFloorSystemLookupStructures({
  rooms,
  planarGraph,
  roomLevelIndices,
}: Pick<MakeFloorSystemOptions, "rooms" | "planarGraph" | "roomLevelIndices">): FloorPathLookupDataStructures {
  const { polygonToPlanarGraph } = planarGraph;

  const planarGraphFaceToRoom: number[] = [];
  const roomToPlanarGraphFace: number[] = [];
  for (const room of rooms) {
    const roomIndexOnLevel = roomLevelIndices[room.id];
    const roomEdge = polygonToPlanarGraph[roomIndexOnLevel][0][0];

    planarGraphFaceToRoom[roomEdge.index] = roomIndexOnLevel;
    roomToPlanarGraphFace[roomIndexOnLevel] = roomEdge.index;
  }

  return {
    rooms,
    planarGraphFaceToRoom,
    roomToPlanarGraphFace,
  };
}

export function makeFloorPaths(opts: Omit<MakeFloorSystemOptions, "floorPaths">): HalfEdgeToFloorPath {
  const ctx = initFloorSystemLookupStructures(opts);
  const { planarGraphFaceToRoom } = ctx;
  const { planarGraph, rooms, wallSystem, settings, roomFloorAssemblies, targetedPorchSettings } = opts;
  const rawPlanarGraph = planarGraph.planarGraph;
  const { halfEdges, halfEdgeParentFace, faceHalfEdges } = rawPlanarGraph;
  const { halfEdgeToWall, walls } = wallSystem;

  const halfEdgeToFloorPath: HalfEdgeToFloorPath = {};

  const isHalfEdgeLineCrossingWallOpeningRect = (
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    rect: Rect2 | null | undefined,
  ): boolean => {
    if (!rect) {
      return false;
    }
    const polyInput = polyLine2ByTwoPoints(startX, startY, endX, endY);
    //by reducing the bounding boxes by a small amount we can avoid the case where the line is
    //exactly on the edge of the rect and the containment check gives crossing output later
    const polyAABB = aabb2ByPolyLine2s([polyInput]);
    const rectAABB = aabb2ByPolyLine2s([rect]);
    if (
      polyAABB &&
      rectAABB &&
      aabb2Overlaps(
        aabb2Grow(polyAABB, -DIST_EPSILON, -DIST_EPSILON),
        aabb2Grow(rectAABB, -DIST_EPSILON, -DIST_EPSILON),
      )
    ) {
      const cont = classifyContainmentPolyLine2PolyLine2(polyInput, rect);

      //only cases we care about is the crossing one because windows and doors from other halfedges
      //will only cross partially (that is a placement limitation for now)
      if (cont === PolyLine2PolyLine2ContainmentClass.CROSSING) {
        return true;
      }
    }
    return false;
  };

  function getCollinearHEIndex(halfEdgeIndex: number, faceIndex: number, next: boolean) {
    let collinearHEIndex = -1;
    if (halfEdgeIndex === -1) {
      return collinearHEIndex;
    }
    if (next) {
      collinearHEIndex = planarGraphNextCollinearHalfEdgeInLoop(
        rawPlanarGraph,
        halfEdgeIndex,
        faceHalfEdges[faceIndex],
      );
    } else {
      collinearHEIndex = planarGraphPrevCollinearHalfEdgeInLoop(
        rawPlanarGraph,
        halfEdgeIndex,
        faceHalfEdges[faceIndex],
      );
    }
    if (collinearHEIndex === -1) {
      const nextHalfEdge = next
        ? planarGraphNextHalfEdgeInLoop(faceHalfEdges[faceIndex], halfEdgeIndex)
        : planarGraphPrevHalfEdgeInLoop(faceHalfEdges[faceIndex], halfEdgeIndex);
      const nextWall = walls[halfEdgeToWall[nextHalfEdge]];
      if (nextWall.wallType === WallType.VOID) {
        const oppToNextHalfEdgeIndex = planarGraphAdjacentHalfEdge(nextHalfEdge);
        const oppFaceIndex = halfEdgeParentFace[oppToNextHalfEdgeIndex];
        collinearHEIndex = next
          ? planarGraphNextHalfEdgeInLoop(faceHalfEdges[oppFaceIndex], oppToNextHalfEdgeIndex)
          : planarGraphPrevHalfEdgeInLoop(faceHalfEdges[oppFaceIndex], oppToNextHalfEdgeIndex);
        if (!planarGraphHalfEdgesCollinear(rawPlanarGraph, collinearHEIndex, halfEdgeIndex)) {
          collinearHEIndex = -1;
        }
      }
    }
    return collinearHEIndex;
  }

  function getCrossingWallsAndDoors(
    halfEdgeIndex: number,
    faceIndex: number,
    next: boolean,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
  ) {
    let wallDoors: GeneratedDoorPlacement[] = [];
    let wallWindows: GeneratedWindowPlacement[] = [];

    const collinearHalfEdgeIndex = getCollinearHEIndex(halfEdgeIndex, faceIndex, next);
    if (collinearHalfEdgeIndex !== -1) {
      const wall = walls[halfEdgeToWall[collinearHalfEdgeIndex]];
      wallDoors = wall?.wallType === WallType.SOLID ? wall.doorPlacements : [];
      wallDoors = wallDoors.filter((door) => {
        if (!door.opening) {
          return false;
        }
        // undefined here should be replace with  door.opening.planExtents OR door.opening.planExtentsCasing;
        // doing so causes faceDiff regressions
        // https://linear.app/higharc/issue/BDG-248/fix-floorsystem-crossing-walls-and-doors-function
        return isHalfEdgeLineCrossingWallOpeningRect(startX, startY, endX, endY, undefined);
      });
      wallWindows = wall.wallType === WallType.SOLID ? wall.windowPlacements : [];
      wallWindows = wallWindows.filter((window) => {
        if (!window.opening) {
          return false;
        }

        // undefined here should be replace with  window.opening.planExtents OR window.opening.planExtentsCasing;
        // doing so causes faceDiff regressions
        // https://linear.app/higharc/issue/BDG-248/fix-floorsystem-crossing-walls-and-doors-function
        return isHalfEdgeLineCrossingWallOpeningRect(startX, startY, endX, endY, undefined);
      });
    }
    return { wallDoors, wallWindows };
  }

  for (let i = 0, l = halfEdges.length; i < l; i += 2) {
    const halfEdgeIndex = i / 2;

    // if half edge is visited, ignore it
    if (halfEdgeToFloorPath[halfEdgeIndex]) {
      continue;
    }

    // get half edge parent face
    const faceIndex = halfEdgeParentFace[halfEdgeIndex];

    // get opposite half edge & face
    const oppHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
    const oppFaceIndex = halfEdgeParentFace[oppHalfEdgeIndex];

    const roomIndex = planarGraphFaceToRoom[faceIndex];
    const oppRoomIndex = planarGraphFaceToRoom[oppFaceIndex];

    const leftRoom = rooms[roomIndex];
    const rightRoom = rooms[oppRoomIndex];

    const leftRoomClass = leftRoom?.attributes.roomClass ?? RoomClass.EXTERIOR;
    const rightRoomClass = rightRoom?.attributes.roomClass ?? RoomClass.EXTERIOR;

    const leftFloorAssembly = roomFloorAssemblies[leftRoom?.id] ?? voidFloorAssemblyNew();
    const rightFloorAssembly = roomFloorAssemblies[rightRoom?.id] ?? voidFloorAssemblyNew();

    const [startX, startY] = planarGraphHalfEdgeStart(rawPlanarGraph, halfEdgeIndex);
    const [endX, endY] = planarGraphHalfEdgeStart(rawPlanarGraph, oppHalfEdgeIndex);

    const wall = walls[halfEdgeToWall[halfEdgeIndex]];

    /*
    In the following, we are collecting doors and windows from the next or previous collinear halfedges
    which are overlapping with the current halfedge. This is to ensure that the doors and windows are
    correctly added to foundation section sizes later for rendering, drawing generation purposes.
    */

    const { wallDoors: nextWallDoors, wallWindows: nextWallWindows } = getCrossingWallsAndDoors(
      halfEdgeIndex,
      faceIndex,
      true,
      startX,
      startY,
      endX,
      endY,
    );

    const { wallDoors: prevWallDoors, wallWindows: prevWallWindows } = getCrossingWallsAndDoors(
      halfEdgeIndex,
      faceIndex,
      false,
      startX,
      startY,
      endX,
      endY,
    );

    let doors = wall?.wallType === WallType.SOLID ? wall.doorPlacements : [];
    doors = [...doors, ...nextWallDoors, ...prevWallDoors];

    let windows = wall.wallType === WallType.SOLID ? wall.windowPlacements : [];
    windows = [...windows, ...nextWallWindows, ...prevWallWindows];

    const { wallDescriptor } = getWallPath(wall);
    const { floorPriority, foundationAssembly, leftSide, rightSide } = wallDescriptor;

    assertIsNotUndefined(foundationAssembly);
    const { floorOffset, leftWallOffset, rightWallOffset } = detectFloorPathOffsets({
      wall,
      leftFloorAssembly,
      rightFloorAssembly,
      foundationAssembly,
      settings,
      targetedPorchSettings,
    });

    const floorPath = floorPathNew({
      halfEdgeIndex,
      planarGraphId: opts.levelId, //note these are keyed by levelId, this may change as well
      leftWallRoom: leftSide.wallRoom,
      rightWallRoom: rightSide.wallRoom,
      leftRoom,
      rightRoom,
      leftRoomClass,
      rightRoomClass,
      leftFloorAssembly,
      rightFloorAssembly,
      floorOffset,
      floorPriority,
      leftWallOffset,
      rightWallOffset,
      doors,
      windows,
    });

    halfEdgeToFloorPath[halfEdgeIndex] = floorPath;
    halfEdgeToFloorPath[oppHalfEdgeIndex] = floorPathOpposite(floorPath);
  }

  return halfEdgeToFloorPath;
}

export function makeFloors(opts: MakeFloorSystemOptions): Floor[][] {
  const {
    levelId,
    levels,
    planarGraph: { planarGraph },
    settings: { foundationSettings },
    lumberCounters,
    floorPaths,
    frameFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
  } = opts;

  const level = levels[levelId];
  const levelIsGround = groundLevelId(levels) === levelId;
  const levelIsBottom = bottomLevelId(levels) === levelId;
  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  const floors: Floor[][] = [];

  const roomClassFloorAssemblies = makeRoomClassFloorAssemblies({
    ...opts,
  });
  const roomClassFloorAssembliesWithHoles = makeRoomClassFloorAssemblies({
    ...opts,
    roomPredicate: (room) => !room.attributes.openToBelow && !levelIsBottom,
  });

  const floorFramingOffset = (halfEdge: number) => {
    const path = floorPaths[halfEdge] ?? floorPaths[planarGraphAdjacentHalfEdge(halfEdge)];
    return path.floorOffset;
  };

  // iterate room classes in order of priority
  for (const roomClass of enumValues(RoomClass, true)) {
    const roomClassFloorAssemblyInfo = roomClassFloorAssemblies[roomClass];
    const roomClassFloorAssemblyInfoWithHoles = roomClassFloorAssembliesWithHoles[roomClass];

    floors[roomClass] = [];

    if (!roomClassFloorAssemblyInfo || roomClass === RoomClass.EXTERIOR) {
      floors[roomClass].push(voidFloorNew({ roomClass }));
      continue;
    }

    for (const floorKey of Object.keys(roomClassFloorAssemblyInfo)) {
      const floorInfo = roomClassFloorAssemblyInfo[floorKey] as RoomClassFloorAssembly;
      const floorFramingInfo = (roomClassFloorAssemblyInfoWithHoles?.[floorKey] ?? floorInfo) as RoomClassFloorAssembly;

      const { planarGraphLoops: planarGraphPerimeterLoops, floorIsSupported, floorAssembly, hashParams } = floorInfo;

      if (floorAssembly.assemblyType === FloorAssemblyType.VOID) {
        continue;
      }

      const { planarGraphLoops: planarGraphPerimeterFramingLoops } = floorFramingInfo;

      let { structuralThickness: floorPlateStructuralThickness, subfloorThickness } =
        floorAssemblyFloorPlateThicknessesByComponents(
          framingComponents,
          slabComponents,
          sheathingComponents,
          floorAssembly,
        );
      const floorPlateTop = floorAssemblyTop(floorAssembly, level);

      assertIsNotUndefined(foundationSystemSettings);
      const roomClassSlabType = slabTypeAtRoomClass(roomClass, foundationSystemSettings);
      const floorHasCrawlspace = roomClassSlabType === SlabType.NONE && levelIsGround && !floorIsSupported;

      if (level.floorIndex <= 0) {
        if (roomClassSlabType !== SlabType.NONE) {
          floorPlateStructuralThickness = 0;
          subfloorThickness = 0;
        }
      }
      const floorThickness = floorPlateStructuralThickness + subfloorThickness;

      const floorPlatePerimeters: PolyLine2[] = [];
      const floorPlateFramingPerimeters: PolyLine2[] = [];

      for (const halfEdges of planarGraphPerimeterLoops.faceHalfEdges) {
        const { pl: floorPlatePerimeter } = offsetPolyLine2VariableStep(
          planarGraphHalfEdgeStripToPolyLine2(planarGraph, halfEdges),
          halfEdges.map(floorFramingOffset),
        );

        floorPlatePerimeters.push(polyLine2SetCCW(floorPlatePerimeter));
      }

      for (const halfEdges of planarGraphPerimeterFramingLoops.faceHalfEdges) {
        floorPlateFramingPerimeters.push(
          polyLine2SetCCW(
            offsetPolyLine2VariableStep(
              planarGraphHalfEdgeStripToPolyLine2(planarGraph, halfEdges),
              halfEdges.map(floorFramingOffset),
            ).pl,
          ),
        );
      }

      const perimeterFaces = makeFloorFaces({
        planarGraph,
        perimeters: planarGraphPerimeterLoops,
        perimeterPolyLine2s: floorPlatePerimeters,
        floorPlateElevation: floorPlateTop,
      });

      const framingPerimeterFaces = makeFloorFaces({
        planarGraph,
        perimeters: planarGraphPerimeterFramingLoops,
        perimeterPolyLine2s: floorPlateFramingPerimeters,
        floorPlateElevation: floorPlateTop,
      });

      const floorPlate = floorPlateNew({
        perimeters: planarGraphPerimeterLoops,
        perimeterPolyLine2s: floorPlatePerimeters,
        perimeterFaces,
        framingPerimeters: planarGraphPerimeterFramingLoops,
        framingPerimeterPolyLine2s: floorPlateFramingPerimeters,
        framingPerimeterFaces,
      });

      const makeFloorExtrusions = (faces: Face[], thickness: number) => {
        if (nonZeroFloorThickness(thickness)) {
          return faces.map((face) => extrudeFace(face, 0, 0, -thickness));
        }
        return [];
      };

      const subFloor = makeFloorExtrusions(framingPerimeterFaces, subfloorThickness);
      const subfloorComponent = sheathingComponents[floorAssembly.subFloor];

      if (floorAssembly.structuralType === StructuralFloorType.FRAMING) {
        const frameFloorAssembly = frameFloorAssemblies[floorAssembly.assemblyId];
        const rimBoardThickness = floorPlateStructuralThickness;
        const framingComponentHeight = floorAssemblyJoistComponentHeight(framingComponents, floorAssembly);
        const joistSpacing = frameFloorAssembly?.joistSpacing ?? 16;
        const spanDirection =
          floorAssembly.spanDirection ?? frameFloorAssembly?.spanDirection ?? SpanDirection.SHORTEST_SPAN;
        const floorFraming = makeFloorFraming({
          framingOutlines: floorPlate.framingPerimeterPolyLine2s,
          floorPlateElevation: floorPlateTop - subfloorThickness,
          floorPlateThickness: rimBoardThickness,
          joistComponentHeight: framingComponentHeight,
          joistSpacing,
          spanDir: spanDirection,
          lumberCounters,
          frameFloorAssembly,
          framingComponents,
        });

        floors[roomClass].push(
          solidFramingFloorNew({
            roomClass,
            hashParams,
            floorElevation: floorPlateTop,
            floorPlate,
            floorPlateThickness: floorThickness,
            floorFraming,
            subFloor,
            subfloorName: subfloorComponent?.name,
            subfloorType: subfloorComponent?.sheathingType,
            subfloorThickness,
            subfloorComponentId: floorAssembly.subFloor,
            crawlSpace: floorHasCrawlspace,
          }),
        );
      } else if (floorAssembly.structuralType === StructuralFloorType.SLAB) {
        const slabFaces = makeFloorFaces({
          planarGraph,
          perimeters: planarGraphPerimeterFramingLoops,
          perimeterPolyLine2s: floorPlateFramingPerimeters,
          floorPlateElevation: floorPlateTop - subfloorThickness,
        });

        const slabStructuralThickness = floorPlateStructuralThickness;
        const slab = makeFloorExtrusions(slabFaces, slabStructuralThickness);

        floors[roomClass].push(
          solidSlabFloorNew({
            roomClass,
            hashParams,
            floorElevation: floorPlateTop,
            floorPlate,
            floorPlateThickness: floorThickness,
            slab,
            subFloor,
            subfloorName: subfloorComponent?.name,
            subfloorType: subfloorComponent?.sheathingType,
            subfloorThickness,
            subfloorComponentId: floorAssembly.subFloor,
            crawlSpace: floorHasCrawlspace,
          }),
        );
      }
    }
  }

  return floors;
}

interface MakeRoomClassFloorAssemblyOptions {
  readonly rooms: Room[];
  readonly roomLevelIndices: Table<number>;
  readonly roomFloorAssemblies: Table<FloorAssembly>;
  readonly roomClassFloorAssemblyGroups: RoomClassFloorAssemblyGroupTable;
  readonly planarGraph: PolygonPlanarGraph;
  readonly planarGraphLevelAssociations: PlanarGraphLevelAssociations;
  readonly roomPredicate?: (room: Room) => boolean;
  readonly splitCanteleveredFloors?: boolean;
}

interface RoomClassFloorAssembly {
  /** @deprecated this is duplicated in hashParams. we should consolidate this. */
  readonly roomClass: RoomClass;
  /** The hash parameters used to group floor assemblies. This type is
   * partial since not all hash parameters are used for every generation context
   * (i.e. analytic floor structures vs physical floor structures).
   */
  readonly hashParams: Partial<FloorAssemblyHashParameters>;
  readonly planarGraphLoops: PlanarGraphLoops;
  readonly floorAssembly: FloorAssembly;
  readonly floorIsSupported: boolean;
}

type RoomClassFloorAssemblies = { [K in RoomClass]?: RoomClassFloorAssembly[] };

function makeRoomClassFloorAssemblies(opts: MakeRoomClassFloorAssemblyOptions): RoomClassFloorAssemblies {
  const {
    rooms,
    roomLevelIndices,
    roomClassFloorAssemblyGroups,
    roomFloorAssemblies,
    planarGraph,
    planarGraphLevelAssociations,
    roomPredicate,
  } = opts;

  function includeRoom(roomId: string) {
    const room = rooms.find((r) => r.id === roomId);

    if (!room) {
      return false;
    }

    if (roomPredicate && !roomPredicate(room)) {
      return false;
    }

    return true;
  }
  const roomClassPlanarGraphLoops: RoomClassFloorAssemblies = {};

  for (const roomClass of enumValues(RoomClass, true)) {
    const roomGroups = roomClassFloorAssemblyGroups[roomClass];

    if (!roomGroups) {
      continue;
    }

    roomClassPlanarGraphLoops[roomClass] = [];
    for (const floorAssemblyGroup in roomGroups) {
      const { hashParams, roomIds } = roomGroups[floorAssemblyGroup];
      const supportedFaces: number[] = [];
      const unsupportedFaces: number[] = [];
      //we have already established that all the rooms in the group have the same floor assembly
      const roomFloorAssembly: FloorAssembly = roomFloorAssemblies[roomIds[0]];
      for (const roomId of roomIds) {
        if (!includeRoom(roomId)) {
          continue;
        }
        const polygonIndex = roomLevelIndices[roomId];
        const faceIndex = planarGraph.polygonToPlanarGraphFace(polygonIndex);
        const facesBelow = planarGraphLevelAssociations.facesBelow[faceIndex] ?? [];
        if (facesBelow.length === 0) {
          unsupportedFaces.push(faceIndex);
        } else {
          supportedFaces.push(faceIndex);
        }
      }
      if (supportedFaces.length !== 0) {
        const planarGraphLoops = planarGraphLoopsReverse(
          planarGraphTraceFaceGroupPerimeter(planarGraph.planarGraph, supportedFaces),
        );
        roomClassPlanarGraphLoops[roomClass].push({
          roomClass,
          hashParams,
          planarGraphLoops,
          floorAssembly: roomFloorAssembly,
          floorIsSupported: true,
        });
      }
      if (unsupportedFaces.length !== 0) {
        const planarGraphLoops = planarGraphLoopsReverse(
          planarGraphTraceFaceGroupPerimeter(planarGraph.planarGraph, unsupportedFaces),
        );
        roomClassPlanarGraphLoops[roomClass].push({
          roomClass,
          hashParams,
          planarGraphLoops,
          floorAssembly: roomFloorAssembly,
          floorIsSupported: false,
        });
      }
    }
  }
  return roomClassPlanarGraphLoops;
}

export function makeFloorFinishes(opts: MakeFloorSystemOptions): FloorFinishTable {
  const {
    rooms,
    roomFloorFinishAssemblies: roomFloorLayouts,
    interiorPalette,
    exteriorPalette,
    roomFloorAssemblies,
  } = opts;

  const floorFinishes: Mutable<FloorFinishTable> = {};

  assertIsNotUndefined(roomFloorLayouts);

  for (const room of rooms) {
    const layouts = roomFloorLayouts ? roomFloorLayouts[room.id] : [];
    const floorAssembly = roomFloorAssemblies[room.id];
    floorFinishes[room.id] = makeRoomFloorFinishes(room, floorAssembly, layouts, interiorPalette, exteriorPalette);
  }

  return floorFinishes;
}

function makeFloorFaces({
  planarGraph,
  perimeters,
  perimeterPolyLine2s,
  floorPlateElevation,
}: {
  planarGraph: PlanarGraph;
  perimeters: PlanarGraphLoops;
  perimeterPolyLine2s: PolyLine2[];
  floorPlateElevation: number;
}): Face[] {
  const solidFloorPlate: Face[] = [];

  const floorPlane = planeNewXY(0, 0, floorPlateElevation);
  const floorHoleIndices: { [index: number]: true } = {};
  const floorPerimeterIndices: { [index: number]: true } = {};

  for (let i = 0, l = perimeters.faceHalfEdges.length; i < l; i++) {
    const framingLoop = perimeters.faceHalfEdges[i];
    const framingLoopPolyline = planarGraphHalfEdgeStripToPolyLine2(planarGraph, framingLoop);
    const framingWinding = polyLine2WindingDir(framingLoopPolyline);

    if (framingWinding === WindingDir.CW) {
      floorHoleIndices[i] = true;
    } else {
      floorPerimeterIndices[i] = true;
    }
  }

  for (const perimeterIndex in floorPerimeterIndices) {
    const subFloorPerimeter = perimeterPolyLine2s[perimeterIndex];
    const subFloorHoles: PolyLine2[] = [];

    for (const holeIndex in floorHoleIndices) {
      const holeCandidate = perimeterPolyLine2s[holeIndex];
      if (
        classifyContainmentPolyLine2PolyLine2Fast(holeCandidate, subFloorPerimeter) !== PolyLine2Containment.OUTSIDE
      ) {
        subFloorHoles.push(polyLine2SetCCW(holeCandidate));
        delete floorHoleIndices[holeIndex];
      }
    }
    const subFloorFace = faceNew([polyLine2SetCW(polyLine2Copy(subFloorPerimeter)), ...subFloorHoles], floorPlane);
    solidFloorPlate.push(subFloorFace);
  }

  return solidFloorPlate;
}
